// import './polyfills';
import App from './app.vue';
import AppInitialLoader from './core/components/app-initial-loader/app-initial-loader.vue';
import { createApp } from 'vue';
import { rootStore } from './store';
import { router } from './router';
import './common/style/index.scss';

import { library, config } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import 'bootstrap/dist/css/bootstrap.min.css';

import '@fortawesome/fontawesome-svg-core/styles.css';

import { worker } from './workerManager';
import { SafeAny } from './common/types';

import {
  faFile,
  faArrowUpRightFromSquare,
  faGear,
  faForward,
  faPlay,
  faPause,
  faArrowUp,
  faPlus,
  faMinus,
  faArrowDown,
  faMinimize,
  faTimes,
  faGripVertical,
  faEyeSlash,
  faEye,
  faFileImport,
  faClone,
  faBan,
  faCheck,
  faSpinner,
  faPalette,
  faMagnifyingGlass,
  faChartSimple,
  faUser,
  faCalendarDay,
  faIdCard,
  faBookMedical,
  faQuestion,
  faHouseUser,
  faInfo,
  faInfoCircle,
  faCheckDouble,
  faArrowsUpDown,
  faRotateLeft,
  faArrowsRotate
} from '@fortawesome/free-solid-svg-icons';
import { capitalizeFirstLetter, configureAmplify, getOrigin, i18n } from './common/utils';

config.autoAddCss = false;

// We should keep this to block styleInjection from dependencies for security purposes.
(window as any).styleInject = function () {};

library.add(
  faFile,
  faArrowUpRightFromSquare,
  faInfo,
  faInfoCircle,
  faGear,
  faForward,
  faPlay,
  faPause,
  faArrowUp,
  faPlus,
  faMinus,
  faArrowDown,
  faMinimize,
  faTimes,
  faGripVertical,
  faEyeSlash,
  faEye,
  faFileImport,
  faCheck,
  faClone,
  faBan,
  faSpinner,
  faPalette,
  faChartSimple,
  faMagnifyingGlass,
  faUser,
  faCalendarDay,
  faIdCard,
  faBookMedical,
  faQuestion,
  faHouseUser,
  faCheckDouble,
  faArrowsUpDown,
  faRotateLeft,
  faArrowsRotate
);

async function preloadState() {
  const { id, band, notch } = router.currentRoute.value.params;

  return new Promise((resolve) => {
    worker.postMessage({
      command: 'getState',
      params: { id, band, notch }
    });

    worker.onmessage = (event) => {
      if (event.data.command === 'stateLoaded') {
        if (event.data.payload) {
          const initState = rootStore?.state;
          const newState: SafeAny = {
            ...initState,
            epoch: {
              ...initState?.epoch,
              epochData: { ...event.data.payload.epochData }
            }
          };
          rootStore.replaceState(newState);
        }
        resolve(true);
      }
    };
  });
}

async function syncUrlWithStoreParams(presetSettings) {
  const currentRoute = router.currentRoute.value;

  if (!presetSettings) return;

  const storeNotch = capitalizeFirstLetter(presetSettings?.notch);
  const storeBand = capitalizeFirstLetter(presetSettings?.band);
  const paramBand = capitalizeFirstLetter(currentRoute.params.band);
  const paramNotch = capitalizeFirstLetter(currentRoute.params.notch);

  if (storeNotch && storeBand && (storeNotch !== paramNotch || storeBand !== paramBand)) {
    await router.replace({
      name: currentRoute.name?.toString(),
      params: {
        ...currentRoute.params,
        notch: storeNotch,
        band: storeBand
      },
      query: currentRoute.query
    });
  }
}

async function init() {
  const loaderApp = createApp(AppInitialLoader);
  loaderApp.mount('#app');

  loaderApp.use(router);
  await router.isReady();

  await preloadState();

  rootStore.watch(
    (state) => state.epoch?.settings,
    (presetSettings) => {
      if (presetSettings && !presetSettings.isLoading) {
        syncUrlWithStoreParams(presetSettings);
      }
    },
    { deep: true }
  );

  loaderApp.unmount();

  const app = createApp(App);

  app.config.globalProperties.$translate = i18n.global.t;

  await configureAmplify();
  rootStore.commit('user/setOrigin', getOrigin());

  app
    .use(rootStore)
    .use(router)
    .use(i18n)
    .component('font-awesome-icon', FontAwesomeIcon)
    .mount('#app');
}

init();
